// src/pages/Moderation/ChannelsViolationsPage/ChannelsViolationsPage.tsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PageHeader } from '../UsersViolationsPage/ui/PageHeader';
import './ChannelsViolationsPage.scss';
import { useChannelsViolations } from './model/useChannelsViolations';
import { ChannelsFilters } from './ui/ChannelsFilters';
import { ChannelsQuickStats } from './ui/ChannelsQuickStats';
import { ChannelsViolationsTable } from './ui/ChannelsViolationsTable';
import { ChannelViolationsDrawer } from './ui/ChannelViolationsDrawer';

import { getModerationReportsRequest } from 'redux/reducers/moderation/reducer';
import { getModerationReportsSelector } from 'redux/reducers/moderation/selectors';
import type { GetReportsResponse, ReportInfo } from 'redux/reducers/moderation/types';

const ChannelsViolationsPage: React.FC = () => {
  const dispatch = useDispatch();

  const {
    channels,
    total,
    counters,
    topRestrictions,
    restrictionsOptions,
    page,
    perPage,
    filters,
    sort,
    setPage,
    setPerPage,
    setFilters,
    setSort,
    flags,
  } = useChannelsViolations();

  const [selected, setSelected] = useState<any | null>(null);

  // ✅ report для Drawer
  const [drawerReport, setDrawerReport] = useState<ReportInfo | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const reportsState = useSelector(getModerationReportsSelector) as GetReportsResponse | null;

  useEffect(() => {
    const channelId = Number(selected?.id);
    if (!channelId || !Number.isFinite(channelId)) {
      setDrawerReport(null);
      return;
    }

    setDrawerLoading(true);
    setDrawerReport(null);

    dispatch(
      getModerationReportsRequest({
        current_page: 1,
        per_page: 1,
        // ✅ варіант A: якщо бекенд підтримує фільтр по peer
        channel_id: channelId,
        // ✅ якщо бекенд це вимагає як і в юзерах — лишай:
        has_restriction: true,
      }),
    );
  }, [dispatch, selected?.id]);

  useEffect(() => {
    if (!drawerLoading) return;

    const list: ReportInfo[] = Array.isArray(reportsState?.data) ? reportsState!.data : [];
    setDrawerReport(list[0] ?? null);
    setDrawerLoading(false);
  }, [reportsState, drawerLoading]);

  return (
    <div className="channels-violations-page">
      <div className="channels-violations-page__content">
        <PageHeader
          title="Channels with violations"
          subtitle="Channels that have restrictions / reports"
        />

        <ChannelsQuickStats
          totalChannels={total}
          channelsWithRestrictions={
            channels.filter((c) => (c.restrictions?.length ?? 0) > 0).length
          }
          counters={counters}
          topRestrictions={topRestrictions}
          onPickRestriction={(r) =>
            setFilters((prev) => ({ ...prev, restriction: r, onlyWithRestrictions: true }))
          }
        />

        <ChannelsFilters
          value={filters}
          sort={sort}
          restrictionOptions={restrictionsOptions}
          onChange={setFilters}
          onSortChange={setSort}
          flags={flags}
          onReset={() =>
            setFilters({
              search: '',
              restriction: undefined,
              flag: undefined,
              dateFrom: undefined,
              dateTo: undefined,
              status: undefined,
            })
          }
        />

        <div className="channels-violations-page__grid">
          <ChannelsViolationsTable
            channels={channels}
            total={total}
            page={page}
            flags={flags}
            perPage={perPage}
            onRowClick={(c) => setSelected(c)}
            onPageChange={setPage}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setPage(1);
            }}
          />

          {selected ? (
            <ChannelViolationsDrawer
              flags={flags}
              channel={selected}
              reportInfo={drawerReport}
              reportLoading={drawerLoading}
            />
          ) : (
            <div className="cv-drawer" style={{ opacity: 0.7 }}>
              <div className="cv-drawer__title">Channel details</div>
              <div className="cv-drawer__row">
                <span className="cv-drawer__k">Tip</span>
                <span className="cv-drawer__v">Click a row to see details</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelsViolationsPage;
